import React, { Component } from 'react';
import { Helmet } from 'react-helmet';

// Layout
import Header from '../layout/header';
import Footer from '../layout/footer';

// Elements
import AvailableCitiesModal from '../elements/available-cities-modal';
import TestimonialSection from '../elements/testimonial';
import { startingPriceLabel } from '../../utils/pricing';

// Premium components
import {
  PremiumHero,
  SectionWrapper,
  StepCards,
  WhyUsGrid,
  FinalCTA,
  Breadcrumbs,
  StructuredData,
} from '../elements/premium';

const MISSION_ITEMS = [
  { icon: '🏠', title: 'Doorstep Convenience', desc: 'No more garage trips. Our mechanics come to your home, office, or wherever you are — fully equipped for any job.' },
  { icon: '💰', title: 'Transparent Pricing', desc: 'See exact costs before you book. No hidden fees, no surprises — ever. What you see is what you pay.' },
  { icon: '🎓', title: 'Certified Mechanics', desc: 'Every technician is trained, verified, and background-checked. Real professionals who take pride in their work.' },
  { icon: '🔒', title: 'Genuine Parts Only', desc: 'We use OEM and genuine spare parts exclusively. Quality you can trust for every repair and replacement.' },
  { icon: '📸', title: 'Full Transparency', desc: 'Before/after photos, real-time updates via our app, and a digital job card for every service — complete visibility.' },
  { icon: '🛡️', title: '30-Day Warranty', desc: 'Every service is backed by a 30-day warranty on parts and labour. Your satisfaction is guaranteed.' },
];

const MILESTONES = [
  { no: '01', icon: '🚀', title: 'Founded', desc: 'Started with a simple mission — make vehicle servicing as easy as ordering food. No middlemen, no workshops, just expert mechanics at your door.' },
  { no: '02', icon: '📱', title: 'App Launch', desc: 'Launched on Android & iOS with real-time tracking, digital job cards, and instant booking — designed for India\'s mobile-first users.' },
  { no: '03', icon: '🏙️', title: '32+ Cities', desc: 'Expanded across India — from metros to tier-2 cities. Same quality, same transparency, same warranty everywhere.' },
  { no: '04', icon: '🎯', title: '2,00,000+ Customers', desc: 'Trusted by over two lakh customers with a 4.8-star rating. Building India\'s most reliable doorstep vehicle service network.' },
];

class AboutUs extends Component {
  render() {
    const { isMobileBrowser, isMobileApp, user, setUser, citiesModal, setCitiesModal } = this.props;

    const schema = {
      '@type': 'Organization',
      name: 'Ride N Repair',
      url: 'https://www.ridenrepair.com',
      description: 'Doorstep bike and car service & repair across 32+ cities in India.',
      foundingDate: '2022',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+91 120 361 5050',
        contactType: 'customer service',
      },
    };

    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <link rel="canonical" href="https://www.ridenrepair.com/about" />
          <title>About Ride N Repair — Doorstep Bike & Car Service Across India</title>
          <meta name="description" content="Learn about Ride N Repair — our mission to make vehicle servicing hassle-free with doorstep bike and car repair across 32+ cities in India. 2,00,000+ customers served." />
          <meta property="og:title" content="About Ride N Repair" />
          <meta property="og:url" content="https://www.ridenrepair.com/about" />
        </Helmet>

        <StructuredData data={schema} />

        {citiesModal && <AvailableCitiesModal closeModal={() => setCitiesModal(false)} />}

        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} />

        <PremiumHero
          compact
          badge="About Us"
          title="Making Vehicle Service Hassle-Free"
          subtitle="Doorstep bike and car repair across 32+ Indian cities — trusted by 2,00,000+ customers"
          isMobileBrowser={isMobileBrowser}
          ctas={[
            { label: 'Book a Service', href: '/service', variant: 'primary' },
            { label: 'Contact Us', href: '/contact', variant: 'secondary' },
          ]}
          trustItems={[
            { value: '2,00,000+', label: 'Customers Served' },
            { value: '4.8★', label: 'Customer Rating' },
            { value: '32+', label: 'Cities' },
            { value: '30-Day', label: 'Warranty' },
          ]}
        />

        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'About Us' }]} />

        {/* OUR STORY */}
        <SectionWrapper kicker="Our Story" heading="Why We Built Ride N Repair">
          <div className="rr-keyword__intro" style={{ maxWidth: 760, margin: '0 auto' }}>
            <p>
              Getting a bike or car serviced in India has always been a frustrating experience — long wait times at
              garages, opaque pricing, questionable parts, and zero transparency into what's actually being done to
              your vehicle. We knew there had to be a better way.
            </p>
            <p>
              <strong>Ride N Repair</strong> was built to solve this. We bring certified, background-verified mechanics
              directly to your doorstep — whether it's your home, office, or anywhere else. Every service comes with
              transparent pricing (shown upfront before you book), genuine parts, real-time tracking via our app, and
              a 30-day warranty.
            </p>
            <p>
              From a two-wheeler-focused startup, we've grown to serve both bikes and cars across <strong>32+ cities
              in India</strong>, with over <strong>2,00,000 happy customers</strong> and a <strong>4.8-star
              rating</strong>. Our mission remains the same: make vehicle maintenance as simple, transparent, and
              trustworthy as it should be.
            </p>
          </div>
        </SectionWrapper>

        {/* OUR JOURNEY */}
        <SectionWrapper variant="dark" kicker="Our Journey" heading="From Startup to 2,00,000+ Customers">
          <StepCards steps={MILESTONES} variant="dark" />
        </SectionWrapper>

        {/* WHAT WE STAND FOR */}
        <SectionWrapper variant="gray" kicker="Our Values" heading="What Makes Us Different">
          <WhyUsGrid items={MISSION_ITEMS} />
        </SectionWrapper>

        {/* TESTIMONIALS */}
        <TestimonialSection isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} />

        {/* CTA */}
        <FinalCTA
          heading={<>Ready to Experience the Difference?<br />Book Your First Service Today.</>}
          subtitle={`Doorstep service · Certified mechanics · Starting ${startingPriceLabel('site')} · 30-day warranty`}
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
          ]}
        />

        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
      </div>
    );
  }
}

export default AboutUs;
